import type { SyncOperationInput, SyncResponseDto, SyncSnapshotView } from '@cuito/types';
import { apiClient } from '../../lib/apiClient';

/**
 * 地点快照 `GET /sync/snapshot?locationId=`（契约 §18.1）。
 *
 * **强制拉取的语义**（`REQ-NFR-012`、风险 R-06 ③）：
 * 收银端**每次进入一个地点**都必须**无条件发一次本请求** —— 深链 `?locationId=`、顶栏切换地点、
 * PWA 冷启动落到 `start_url=/cashier` 之后解析出地点，三条路径都算「进入」。
 * 这次请求**不得**被「反正缓存里有」跳过：在线时的权威源永远是这一次响应，
 * Service Worker 的 `cuito-snapshot`（`StaleWhileRevalidate`）与 IndexedDB 里的旧快照
 * **只服务于断网兜底**。跳过它的症状是「店员进了地点、面板显示的是三天前的价格」——
 * 界面不报错、测试全绿，而价格是错的（这正是 R-06 登记的那一类）。
 *
 * 因此本函数**没有** `force` 参数：它只有一种语义。写成一个可选的开关，
 * 迟早会有人传 `false`，而「传 false 时面板显示旧价」不会有任何断言拦得住。
 *
 * ⚠️ **SW 层缓存的边界（如实登记）**：`apps/tenant/vite.config.ts` 的 `runtimeCaching` 顺序与
 * handler 由 Task 7 冻结（`cuito-media` → `cuito-snapshot` → `cuito-api`；静态⑨ 判字节偏移顺序），
 * 本任务**不动它**。⇒ 在 SW 已就绪的浏览器里，`StaleWhileRevalidate` 仍可能把**上一次**的响应
 * 作为本次的返回值返回（同时在后台 revalidate）。本任务落地的是契约里**定义式的那一条**：
 * 「每次进入地点都无条件发一次请求」（可观察判据 = 请求本身，见 `CashierLayout` 的进入地点 effect）。
 * 把 SW 那层的读取也豁免掉要改运行时缓存规则，属于 Task 7 的冻结面。
 */
export function fetchSnapshot(locationId: string): Promise<SyncSnapshotView> {
  return apiClient.get<SyncSnapshotView>('/sync/snapshot', { params: { locationId } });
}

/**
 * 离线操作补传 `POST /orders/sync`（契约 §18.2）—— 队列里攒下的操作在这里出网。
 *
 * 三件事与本函数的形状有关，都不是风格问题：
 *
 * ① **路径是 `/orders/sync`，不是 `/sync/operations`**：契约 §18.2 那一行的标题就是
 *    `POST /orders/sync`，而权限键是既有的 `order:accept`（Phase 9 **不新增权限键**，
 *    收银端离线队列页因此也用 `order:accept`）。写成「对称的」`/sync/*` 会 404，
 *    而 404 在队列页上表现为「同步永远失败」。
 * ② **`deviceId` 必填且是 UUID**（`@IsUUID()`）：它取 `readDeviceId()`（`cuito-cashier-device`，
 *    首次生成后永不变），不是每次现造一个 —— 现造的话服务端那条「离线补传记录设备标识」
 *    的审计线索（§24.1）每次都是一台新设备。
 * ③ **一次可以带多条**（`operations[]`，`@ArrayMinSize(1)` / `@ArrayMaxSize(200)`），
 *    但收银端**逐条**发（见 `useOfflineQueue`）：契约 §18.2 要求冲突/找不到的那些
 *    「保留本地记录供对账」，而整批发送时服务端仍返 200 + 逐条 `results[]`，
 *    客户端却拿不到「哪一条该留在队列里」之外的任何好处 —— 逐条发让
 *    `syncPendingOrders` 的「一条失败不影响后续」直接等于契约的五种 `status`。
 */
export function syncOperations(deviceId: string, operations: SyncOperationInput[]): Promise<SyncResponseDto> {
  return apiClient.post<SyncResponseDto>('/orders/sync', { deviceId, operations });
}
